// ADOPT AT SAVE — the one definition, for every path that saves a bin's feed.
//
// WHY THIS FILE EXISTS. There are three places a customer can set what is in a
// bin: the add-feed dialog, onboarding's bin builder, and the recovery picker
// under a not-deliverable bin on step 3. All three render the SAME picker, so
// all three could offer the 1,060-row distributor catalog. Only the dialog knew
// that a catalog pick has to be ADOPTED before it means anything, so the other
// two silently dropped it: the bin saved with a free-text product name, nothing
// set FeedBin.feed_product, and store_product_for_bin returns None on a null
// feed_product. The customer saw "we don't recognise this bin's feed" for a
// product they had just picked out of our own catalog.
//
// A catalog Product is NOT a FeedProduct. Picking one selects a row in the
// distributor catalog; adoption is what mints the FeedProduct, promotes it to a
// sellable StoreProduct, and maps the two. Without that the chain has no first
// link, so nothing further can be wrong yet - it never started.
//
// MINTING HAPPENS AT SAVE, NOT AT SELECTION. Minting as the customer scrolls
// would litter FeedProduct with bags nobody chose.
import { deliveryAPI } from '../../../services/deliveryAPI';
import { FeedSelection } from './FeedProductPicker';

export interface ResolvedFeed {
  /** The FeedProduct id to hang on the bin, or undefined for a custom entry. */
  feedProductId?: number;
  /** Set only when the customer needs to know the bag is not ready to deliver. */
  notice: string | null;
}

/** Turn a picker selection into something a bin can be saved with. */
export async function resolveFeedProduct(sel: FeedSelection): Promise<ResolvedFeed> {
  // A branded FeedProduct was picked directly - already the thing bins point at.
  if (sel.selectedProductId) {
    return { feedProductId: sel.selectedProductId, notice: null };
  }
  // "Other / unlisted" is a deliberate free-text entry. It has no product and
  // is not an error; the bin is simply not deliverable, and says so honestly.
  if (!sel.catalogProductId) {
    return { feedProductId: undefined, notice: null };
  }
  const adopted = await deliveryAPI.adoptCatalogProduct(sel.catalogProductId);
  return {
    feedProductId: adopted.feed_product_id ?? undefined,
    // A bag that IS ready to deliver needs no confirmation screen. Only the
    // ones that need setting up are worth stopping the customer for.
    notice: adopted.deliverable ? null : adopted.message,
  };
}

/** Whether a selection is complete enough to save. */
export function hasFeedChoice(sel: FeedSelection): boolean {
  return !!sel.selectedProductId || !!sel.catalogProductId || sel.showOtherInput;
}
